{/* Copyright 2026 Adobe. All rights reserved.
This file is licensed to you under the Apache License, Version 2.0 (the "License");
you may not use this file except in compliance with the License. You may obtain a copy
of the License at http://www.apache.org/licenses/LICENSE-2.0
Unless required by applicable law or agreed to in writing, software distributed under
the License is distributed on an "AS IS" BASIS, WITHOUT WARRANTIES OR REPRESENTATIONS
OF ANY KIND, either express or implied. See the License for the specific language
governing permissions and limitations under the License. */}

import {InstallCommand} from '../../../src/InstallCommand';

import {Layout} from '../../../src/Layout';
export default Layout;

import docs from 'docs:@react-spectrum/s2';

export const hideNav = true;
export const section = 'Releases';
export const tags = ['release', 'S2'];
export const date = 'October 8, 2026';
export const title = 'v1.8.0';
export const description = "This release introduces the new SideNavPanel component, adds async and virtualized support for Menu, and brings several exciting updates to React Spectrum's AI components!";
export const isSubpage = true;

# v1.8.0

This release introduces [SideNavPanel](../SideNavPanel), a component that wraps SideNav and other app chrome in a container that can collapse into a compact icon rail. 

```tsx render hideCode type="s2" dir="s2/releases" files={['packages/dev/s2-docs/pages/s2/SideNavPanelApp.tsx']}
"use client";
import {SideNavPanel, SideNav, SideNavItem, SideNavItemContent, SideNavItemLink, SideNavSection, SideNavHeader, Text} from '@react-spectrum/s2/SideNavPanel';
import {SideNavPanelApp} from '../SideNavPanelApp';
import {style} from '@react-spectrum/s2/style' with {type: 'macro'};
import CCLibrary from '@react-spectrum/s2/icons/CCLibrary';
import Files from '@react-spectrum/s2/icons/Files';
import Images from '@react-spectrum/s2/icons/Images';
import Project from '@react-spectrum/s2/icons/Project';

<SideNavPanelApp defaultSelectedRoute="/files">
  {({selectedRoute}) => (
    /*- begin highlight -*/
    <SideNavPanel
      defaultCollapsed
      styles={style({gridArea: 'sidebar', width: 208, marginX: 8})}>
    {/*- end highlight -*/}
      <SideNav
        aria-label="Files"
        selectedRoute={selectedRoute}
        defaultExpandedKeys={['projects']}
        styles={style({width: 'full'})}>
        <SideNavItem href="/files" textValue="Files">
          <SideNavItemContent>
            <SideNavItemLink>
              <Files />
              <Text>Files</Text>
            </SideNavItemLink>
          </SideNavItemContent>
        </SideNavItem>
        <SideNavItem id="your-libraries" href="/your-libraries" textValue="Your Libraries">
          <SideNavItemContent>
            <SideNavItemLink>
              <CCLibrary />
              <Text>Your Libraries</Text>
            </SideNavItemLink>
          </SideNavItemContent>
          <SideNavItem href="/photos" textValue="Photos">
            <SideNavItemContent>
              <SideNavItemLink>
                <Images />
                <Text>Photos</Text>
              </SideNavItemLink>
            </SideNavItemContent>
          </SideNavItem>
        </SideNavItem>
        <SideNavSection>
          <SideNavHeader>Work</SideNavHeader>
          <SideNavItem id="projects" href="/projects" textValue="Projects">
            <SideNavItemContent>
              <SideNavItemLink>
                <Project />
                <Text>Projects</Text>
              </SideNavItemLink>
            </SideNavItemContent>
            <SideNavItem href="/projects/website" textValue="Website redesign">
              <SideNavItemContent>
                <SideNavItemLink>
                  <Text>Website redesign</Text>
                </SideNavItemLink>
              </SideNavItemContent>
            </SideNavItem>
            <SideNavItem href="/projects/mobile" textValue="Mobile app">
              <SideNavItemContent>
                <SideNavItemLink>
                  <Text>Mobile app</Text>
                </SideNavItemLink>
              </SideNavItemContent>
            </SideNavItem>
          </SideNavItem>
        </SideNavSection>
      </SideNav>
    </SideNavPanel>
  )}
</SideNavPanelApp>
```

It also adds [async loading](../Menu#asynchronous-loading) and [virtualization support](../Menu#virtualized-content) to Menu, and updates the TableView [column menu icon](../TableView#column-menus).

## AI Components

This release introduces the new [AttachmentGrid](../ai-components#chat-threads) component for displaying collections of attachments, adds disabled state support to [Attachment](../ai-components#attachments-and-actions), and enables attachment-only submissions in PromptField. It also improves accessibility with updated PromptField ARIA labels, fine-tunes the visual layout of UserMessage with updated padding, and includes several bug fixes!

### Chat/Thread API Updates
The Chat and Thread APIs have been simplified, with more styling now handled by the components rather than requiring custom styles. Components like ThreadScrollButton are now automatically included in the Thread and must be removed from your implementation. See the updated [documentation](../ai-components) for usage examples.

## Changelog

### General Changes
- Add `scrollbarGutter` to style macro theme - [@yihuiliao](https://github.com/yihuiliao) - [PR](https://github.com/adobe/react-spectrum/pull/10682)
- Update links to the new Spectrum documentation - [@misterbrownlee](https://github.com/misterbrownlee) - [PR](https://github.com/adobe/react-spectrum/pull/10691)
### AI Components
- Add `AttachmentGrid` component - [@DPandyan](https://github.com/DPandyan) - [PR](https://github.com/adobe/react-spectrum/pull/10561)
- Add `AttachmentBadge` - [@DPandyan](https://github.com/DPandyan) - [PR](https://github.com/adobe/react-spectrum/pull/10594)
- Add disabled-state support to `AttachmentPreview` - [@DPandyan](https://github.com/DPandyan) - [PR](https://github.com/adobe/react-spectrum/pull/10594)
- Export `scrollFade` - [@DPandyan](https://github.com/DPandyan) - [PR](https://github.com/adobe/react-spectrum/pull/10594)
- Refine Chat API - [@devongovett](https://github.com/devongovett) - [PR](https://github.com/adobe/react-spectrum/pull/10577)
- Improve accessibility labels in PromptField - [@devongovett](https://github.com/devongovett) - [PR](https://github.com/adobe/react-spectrum/pull/10693)
- Update UserMessage padding - [@devongovett](https://github.com/devongovett) - [PR](https://github.com/adobe/react-spectrum/pull/10693)
- Prevent PromptFieldVoiceButton from overwriting the prompt after submission while dictating- [@yihuiliao](https://github.com/yihuiliao) - [PR](https://github.com/adobe/react-spectrum/pull/10624)
- Add `onExpandedChange` to ResponseStatus - [@LFDanLu](https://github.com/LFDanLu) - [PR](https://github.com/adobe/react-spectrum/pull/10614)
- Prevent Autocomplete key events from triggering PromptTokenField’s onKeyDownhandler - [@LFDanLu](https://github.com/LFDanLu) - [PR](https://github.com/adobe/react-spectrum/pull/10614)
- Allow users to submit only attachments in PromptField - [@LFDanLu](https://github.com/LFDanLu) - [PR](https://github.com/adobe/react-spectrum/pull/10703)
### Icons
- Upgrade open-source workflow icons package - [@yihuiliao](https://github.com/yihuiliao) - [PR](https://github.com/adobe/react-spectrum/pull/10586)
### Menu
- Add async loading support - [@LFDanLu](https://github.com/LFDanLu) - [PR](https://github.com/adobe/react-spectrum/pull/10541), [PR](https://github.com/adobe/react-spectrum/pull/10609)
- Add virtualization support - [@LFDanLu](https://github.com/LFDanLu) - [PR](https://github.com/adobe/react-spectrum/pull/10614), [PR](https://github.com/adobe/react-spectrum/pull/10673)
### SideNavPanel
- Add `SideNavPanel` component - [@snowystinger](https://github.com/snowystinger) - [PR](https://github.com/adobe/react-spectrum/pull/10421), [PR](https://github.com/adobe/react-spectrum/pull/10678)
### TableView
- Update column menu icon - [@yihuiliao](https://github.com/yihuiliao) - [PR](https://github.com/adobe/react-spectrum/pull/10586)
